import * as Sentry from "@sentry/react";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { RootState } from "store/rootReducer";

import { BulkUpdateRequest } from "api/data";

import { EntityKind } from "models/entityKind";
import { PolygonFilter } from "models/filter";

import { useGroupByState } from "components/groupBy/group-by.context";
import { useSelectedCategory } from "components/groupBy/hooks";

import { changedCellsAtom, columnsAtom } from "../atoms/fieldGroupAtoms";
import { useBulkUpdateFieldGroupMutation } from "../mutations/useBulkUpdateFieldGroupMutation";
import { useFieldGroupData } from "./useFieldGroupData";
import { useGetChangedCells } from "./useGetChangedCells";

// 50MB limit to match backend
export const MAX_PAYLOAD_SIZE = 50 * 1024 * 1024;

/**
 * Estimates the JSON payload size
 */
export function estimateRequestSize(request: BulkUpdateRequest): number {
  const jsonString = JSON.stringify(request);
  return new Blob([jsonString]).size;
}

export interface UseFieldGroupSaveParams {
  fieldGroup: string;
  isVisible: boolean;
}

/**
 * Hook that handles field group save operations
 */
export function useFieldGroupSave({ fieldGroup, isVisible }: UseFieldGroupSaveParams) {
  const fieldGroupData = useFieldGroupData({ fieldGroup, isVisible });

  const { entityKind } = useGroupByState();
  const { isOrgDefinedColumn, isProjectDefinedColumn } = useSelectedCategory();
  const isOrg = isOrgDefinedColumn;

  const projectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );
  const wellFilterId = useSelector((state: RootState) => state.filter.filterId);
  const facilityFilterId = useSelector(
    (state: RootState) => state.facilityFilter.filterId
  );
  const filterId =
    entityKind === EntityKind.Well ? wellFilterId?.id : facilityFilterId?.id;
  const polygonFilter = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.filter.polygonFilter
      : state.facilityFilter.polygonFilter
  ) as PolygonFilter | null;

  const effectiveProjectId = isProjectDefinedColumn ? projectId : null;

  const columns = useAtomValue(columnsAtom);
  const resetChangedCells = useResetAtom(changedCellsAtom);
  const changedCells = useGetChangedCells();

  const [validationError, setValidationError] = useState<string | null>(null);
  const [duplicateColumnName, setDuplicateColumnName] = useState<string | null>(null);

  const bulkUpdateMutation = useBulkUpdateFieldGroupMutation({
    fieldGroup,
    entityKind,
    isOrg,
    filterId: filterId ?? "",
    projectId: effectiveProjectId,
    polygonFilter,
    onSuccess: async () => {
      resetChangedCells();
      toast.success(`Successfully updated "${fieldGroup}" data.`);
    },
    onError: (err) => {
      const errorMessage = err.response?.data?.error || "Failed to save changes";

      Sentry.captureException(err, {
        tags: { component: "FieldGroupEditor", action: "save" },
        extra: { fieldGroup, entityKind }
      });

      setValidationError(errorMessage);
      toast.error(errorMessage);
    }
  });

  const handleSave = useCallback(async (): Promise<boolean> => {
    setValidationError(null);
    setDuplicateColumnName(null);

    // Get initial data to compare changes
    if (!fieldGroupData.data) {
      setValidationError("No initial data loaded");
      return false;
    }

    const initialData = fieldGroupData.data;

    // Calculate new columns (columns not in initial data)
    const newColumns: BulkUpdateRequest["newColumns"] = columns
      .filter((col) => !initialData.columns.some((ic) => ic.name === col.name))
      .map((col) => ({
        name: col.name,
        ...(col.defaultUnit && { defaultUnit: col.defaultUnit })
      }));

    // Note: newRows handling not yet implemented
    const newRows: BulkUpdateRequest["newRows"] = [];

    const bulkUpdateRequest: BulkUpdateRequest = {
      entityKind,
      fieldGroup,
      isOrg,
      projectId: effectiveProjectId,
      updates: changedCells,
      newColumns,
      newRows
    };

    const payloadSize = estimateRequestSize(bulkUpdateRequest);
    if (payloadSize > MAX_PAYLOAD_SIZE) {
      const sizeMB = (payloadSize / 1024 / 1024).toFixed(1);
      const errorMsg = `Save operation exceeds size limit (${sizeMB}MB). Maximum allowed is 50MB. Please reduce the number of changes or save in smaller batches.`;
      setValidationError(errorMsg);
      toast.error(errorMsg, { autoClose: 10000 });
      return false;
    }

    try {
      await bulkUpdateMutation.mutateAsync({
        request: bulkUpdateRequest,
        entityKind,
        isOrg,
        projectId: effectiveProjectId
      });

      return true;
    } catch (err) {
      return false;
    }
  }, [
    fieldGroupData.data,
    entityKind,
    isOrg,
    effectiveProjectId,
    changedCells,
    columns,
    fieldGroup,
    bulkUpdateMutation
  ]);

  const clearValidationError = useCallback(() => {
    setValidationError(null);
    setDuplicateColumnName(null);
  }, []);

  return {
    isSaving: bulkUpdateMutation.isLoading,
    validationError,
    duplicateColumnName,
    handleSave,
    clearValidationError
  };
}
